import {
  BrowserClient,
  Scope,
  defaultStackParser,
  linkedErrorsIntegration,
  makeFetchTransport,
} from "@sentry/react";

import type { Environment } from "../../types";
import { resolveEnvironment } from "../environment";
import { DAPP_UI_VERSION } from "../version";

// DSN is a public value; the auth token for source-map upload must never live in code.
const DAPP_UI_SENTRY_DSN =
  "https://5c82aeb32da33b5a0a14b7c5393992ca@o4511668907081728.ingest.de.sentry.io/4511705324126289";

// Sentry rejects slashes in release names, so the npm package name can't be used.
// Must stay in sync with the sentry-cli upload release in tsup.config.ts.
const DAPP_UI_SENTRY_RELEASE = `dapp-ui@${DAPP_UI_VERSION}`;

type SentryEnvironment = "dev" | "stg" | "prod";

export interface InitDappUiSentryOptions {
  /** Falls back to VITE_CROSSX_ENVIRONMENT / NEXT_PUBLIC_CROSSX_ENVIRONMENT detection. */
  environment?: Environment | "dev" | "stg" | "staging" | "prod" | "prd";
  /** Defaults to true only on prod; dev/stg init but do not send. */
  enabled?: boolean;
  /** Override DSN (tests / self-hosted relay). */
  dsn?: string;
  /** 0–1 sample rate for ui:/funnel: analytics events (default 1). Errors are never sampled. */
  analyticsSampleRate?: number;
}

let dappUiScope: Scope | null = null;
let analyticsSampleRate = 1;

export function getAnalyticsSampleRate(): number {
  return analyticsSampleRate;
}

function toSentryEnvironment(environment: Environment): SentryEnvironment {
  switch (environment) {
    case "dev":
      return "dev";
    case "stage":
      return "stg";
    default:
      return "prod";
  }
}

/**
 * dapp-ui is embedded in host apps that may run their own Sentry, so we never
 * call `Sentry.init()`: it claims the global hub and either clobbers the host
 * client or gets clobbered by it. Instead we keep a dedicated BrowserClient on
 * an isolated Scope, with no global integrations (no window.onerror, no
 * fetch/XHR patching) — errors reach the dapp-ui project only through explicit
 * captureDappUiException calls, and the host's Sentry is never touched.
 */
export function initDappUiSentry(options?: InitDappUiSentryOptions): Scope | null {
  if (dappUiScope) return dappUiScope;
  if (typeof window === "undefined") return null;

  const environment = toSentryEnvironment(resolveEnvironment(options?.environment));

  if (typeof options?.analyticsSampleRate === "number") {
    analyticsSampleRate = Math.min(1, Math.max(0, options.analyticsSampleRate));
  }

  // No dedupeIntegration: it drops consecutive identical events, which would
  // swallow repeated ui:/funnel: analytics messages (same button clicked twice).
  const client = new BrowserClient({
    dsn: options?.dsn ?? DAPP_UI_SENTRY_DSN,
    transport: makeFetchTransport,
    stackParser: defaultStackParser,
    integrations: [linkedErrorsIntegration()],
    environment,
    release: DAPP_UI_SENTRY_RELEASE,
    enabled: options?.enabled ?? environment === "prod",
  });

  const scope = new Scope();
  scope.setClient(client);
  scope.setTags({ package: "dapp-ui", framework: "react", source: "dapp-ui" });
  client.init();

  dappUiScope = scope;
  return dappUiScope;
}

export function getDappUiSentryScope(): Scope | null {
  return dappUiScope;
}

/** Lazily initializes with defaults so error boundaries work without host setup. */
export function captureDappUiException(
  error: unknown,
  extra?: Record<string, unknown>,
): string | undefined {
  const scope = dappUiScope ?? initDappUiSentry();
  if (!scope) return undefined;
  return scope.captureException(error, extra ? { captureContext: { extra } } : undefined);
}
